import React from 'react';
import { Modal, Skeleton, Notification } from 'is24-corecss';
import { css } from '@emotion/css';
import { MAGENTA_TV_PRODUCT_MAPPING, EXPIRY_DATE } from '../MagentaTv';
import { useIntl } from '@/i18n/hooks/useIntl';
import { default as Msg } from '@/i18n/components/FormattedMessage';
import { useGetMagentaDiscount } from '@/apiHooks/magentaTv.hook';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';
import { report } from '@/tracking/tracking';
import { BenefitsListPageType } from '@/@types/benefitsListPage';
import { useUrlState } from '@/customHooks/useUrlState.hook';
import { useElementDarkMode } from '@/pages/tenant/hooks/shared/useElementDarkMode';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';

const styles = {
  title: css`
    font-weight: 400;
    font-size: 20px;
    line-height: 30px;
  `,
  subtitle: css`
    font-weight: 400;
    font-size: 16px;
    line-height: 26px;
  `,
  errorMessage: css`
    align-items: flex-start !important;
    &:before {
      top: 6px;
    }
  `,
};

export const TRACKING_PARAM1_MAPPING: { [x: string]: 'rent' | 'living' | 'buy' } = {
  [BenefitsListPageType.MieterPlus.toLowerCase()]: 'rent',
  [BenefitsListPageType.WohnenPlus.toLowerCase()]: 'living',
  [BenefitsListPageType.KauferPlus.toLowerCase()]: 'buy',
};

const VoucherDisplayModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useIntl();
  const isMeinPlus = useHasMeinPlus();
  const { data, isError, initializing } = useGetMagentaDiscount();
  const { parentPath } = useUrlState();
  useElementDarkMode('.modal-voucher-display');
  useModalFocus(true);
  const productConfig =
    MAGENTA_TV_PRODUCT_MAPPING[parentPath] || MAGENTA_TV_PRODUCT_MAPPING[BenefitsListPageType.WohnenPlus.toLowerCase()];

  const handleRedeemClick = () => {
    const parameter1 =
      TRACKING_PARAM1_MAPPING[parentPath?.toLowerCase() || BenefitsListPageType.WohnenPlus.toLowerCase()] || 'rent';
    report({ ...TrackingEvents.MAGENTA_TV.DISCOUNT_CODE_REDEEM, parameter1 }, isMeinPlus);
    if (productConfig?.productPageUrl) {
      window.open(productConfig.productPageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className='padding-top-m padding-bottom-m'>
      <Modal.Root isOpen={true} onClose={onClose}>
        <Modal.Backdrop className='modal-voucher-display'>
          <Modal.Surface>
            <Modal.Header>
              <Modal.TitleGroup>
                <Modal.Title>
                  {t({
                    id: 'benefits.magentaTv.headline',
                    defaultMessage: 'MagentaTV Vorteil',
                  })}
                </Modal.Title>
              </Modal.TitleGroup>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              {isError ? (
                <Notification
                  className={styles.errorMessage}
                  title={
                    <Msg
                      id='benefits.magentaTv.modal.voucher.errorMessage'
                      defaultMessage='Aktuell sind keine Gutscheincodes verfügbar.'
                      values={{
                        br: '<br />',
                      }}
                    />
                  }
                  type='error'
                  variant='flat'
                  size='small'
                />
              ) : (
                <>
                  <p
                    className={`margin-bottom-l font-heading-small-regular display-flex grid-align-center ${styles.title}`}
                  >
                    <Msg
                      id='benefits.magentaTv.modal.voucher.title'
                      defaultMessage='Dein persönlicher Gutscheincode:'
                    />
                    &nbsp;
                    {initializing ? (
                      <Skeleton type='rounded' width='115px' height='25px' className='margin-left-s' />
                    ) : (
                      <strong>{data?.discountCode}</strong>
                    )}
                  </p>
                  <p className={`margin-bottom-l ${styles.subtitle}`}>
                    {t({
                      id: 'benefits.magentaTv.modal.voucher.subtitle',
                      defaultMessage: `Kopiere deinen Gutscheincode und löse ihn bis zum ${EXPIRY_DATE} ein. Hinweis: Du kannst nur einen Gutscheincode erstellen.`,
                    })}
                  </p>
                  <Notification
                    title={t({
                      id: 'benefits.magentaTv.modal.voucher.disclaimer',
                      defaultMessage: 'Hinweis: Du kannst nur einen Gutscheincode erstellen.',
                    })}
                    type='info'
                    variant='flat'
                    size='small'
                  />
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              {
                <Modal.PrimaryFooterButton
                  label={t({
                    id: 'benefits.magentaTv.modal.buttonCta.redeemVoucher',
                    defaultMessage: 'Gutschein einlösen',
                  })}
                  disabled={isError}
                  onClick={handleRedeemClick}
                />
              }
            </Modal.Footer>
          </Modal.Surface>
        </Modal.Backdrop>
      </Modal.Root>
    </div>
  );
};

export default VoucherDisplayModal;
